import {
  Button,
  Checkbox,
  CheckboxProps,
  createForm,
  Flex,
  Heading,
  Link,
  Text,
  TextInput,
  TextInputProps,
  useToasts,
} from '@applyboard/crystal-ui'
import { useGetSpaConfig, useSignUp } from '../../hooks'
import { emailValidate } from '../../utils/fieldValidations'
import { LOCAL_STORAGE_REDIRECT_KEY } from '../../components/ProtectedRoute'
import { ComponentType } from 'react'

type SignUpFormFields = {
  givenName: string
  familyName: string
  email: string
  agreement: boolean
}

const { Form, Field, useFieldValues } = createForm<SignUpFormFields>()

type SignUpFormProps = {
  onSuccess: (id: string) => void
}

export function SignUpForm(props: SignUpFormProps) {
  const toast = useToasts()
  const { isSigningUp, signUp } = useSignUp()
  const { policy } = useGetSpaConfig()

  return (
    <>
      <Flex gap={10} direction="column">
        <Flex justify="center">
          <Heading level={1} variant="headlineM">
            Create account
          </Heading>
        </Flex>
        <Form
          defaultValues={{
            givenName: '',
            familyName: '',
            email: '',
            agreement: false,
          }}
          onSubmit={({ givenName, familyName, email }) => {
            const redirectTo = window.localStorage.getItem(LOCAL_STORAGE_REDIRECT_KEY)

            signUp(
              {
                givenName,
                familyName,
                email,
                included: {
                  termsOfUse: policy.termsOfUse
                    ? {
                        id: policy.termsOfUse.id,
                        acceptedAt: new Date().toISOString(),
                        version: policy.termsOfUse.policyVersion,
                        url: policy.termsOfUse.url,
                      }
                    : undefined,
                  privacyPolicy: {
                    id: policy.privacyPolicy.id,
                    acceptedAt: new Date().toISOString(),
                    version: policy.privacyPolicy.policyVersion,
                    url: policy.privacyPolicy.url,
                  },
                },
              },
              {
                onSuccess: response => {
                  props.onSuccess(response.data.id)
                },
                onError: err => {
                  if (err instanceof Error) {
                    toast.negative(new Error(err.message))
                  }
                },
              },
            )
          }}
        >
          <Flex gap={{ xs: 6, sm: 4 }} direction="column">
            <Flex gap={6} direction={{ xs: 'column', sm: 'row' }}>
              <Flex.Item grow={1}>
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Given Name"
                  name="givenName"
                  required="Given name is required"
                  validate={(value: string) => {
                    return !!value.length || 'This field is required'
                  }}
                />
              </Flex.Item>
              <Flex.Item grow={1}>
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Family Name"
                  name="familyName"
                />
              </Flex.Item>
            </Flex>
            <Field
              as={TextInput as ComponentType<TextInputProps>}
              label="Email"
              name="email"
              required="Email is required"
              validate={(value: string) => {
                return emailValidate(value) || 'This email is invalid.'
              }}
            />
            <Field
              as={Checkbox as ComponentType<CheckboxProps>}
              label={
                <Text>
                  By continuing with the sign-up process, you confirm that you have read and
                  understood the{' '}
                  {policy.termsOfUse?.url ? (
                    <>
                      <Link
                        mode="override_user_choice_and_open_in_new_tab"
                        href={policy.termsOfUse.url}
                        rel="noopener noreferrer"
                      >
                        Terms of Use
                      </Link>
                      {' and '}
                    </>
                  ) : null}
                  <Link
                    mode="override_user_choice_and_open_in_new_tab"
                    href={policy?.privacyPolicy?.url}
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                  . If you do not agree with any part of the document(s), you must discontinue the
                  registration process.
                </Text>
              }
              name="agreement"
            />
            <Flex justify="center" pt={{ xs: 4, sm: 6 }}>
              <SubmitButton loading={isSigningUp} />
            </Flex>
          </Flex>
        </Form>
      </Flex>
      <Flex justify="center" pt={{ xs: 8, sm: 4 }}>
        <Text>
          Already have an account? <Link href="/login">Login</Link>
        </Text>
      </Flex>
    </>
  )
}

type SubmitButtonProps = {
  loading: boolean
}

function SubmitButton(props: SubmitButtonProps) {
  const { givenName, email, agreement } = useFieldValues(['givenName', 'email', 'agreement'])

  return (
    <Button
      intent="primary"
      width="fill"
      type="submit"
      disabled={!givenName.length || !emailValidate(email) || !agreement}
      loading={props.loading}
    >
      Next
    </Button>
  )
}
